import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Flex,
  Text,
  Collapse,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useEntriesColRef, useCompanyColRef, useAppToast } from '../../Hooks'
import { addDoc, query, orderBy, onSnapshot, Timestamp, getDocs, deleteDoc, doc } from 'firebase/firestore'
import { EntryType, CompanyType } from '../../types'

interface EntryFormData {
  date: Date
  amount: number
  currency: string
  companyId: string
  exchangeRate?: number // Optional for RON currency
}

// Removed ExchangeRateData interface - no longer needed



const CompanyEntries = () => {
  const [entries, setEntries] = useState<EntryType[]>([])
  const [companies, setCompanies] = useState<CompanyType[]>([])
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [isLoading, setIsLoading] = useState(false)
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null)
  const { isOpen: isFormOpen, onToggle: onFormToggle } = useDisclosure({ defaultIsOpen: false })
  const { isOpen: isDeleteAlertOpen, onOpen: onDeleteAlertOpen, onClose: onDeleteAlertClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const entriesColRef = useEntriesColRef()
  const companyColRef = useCompanyColRef()
  const toast = useAppToast()

  const {
    register,
    handleSubmit,
    reset,
    watch,  // Add watch function to get form values in real-time
  } = useForm<EntryFormData>()

  useEffect(() => {
    // Load companies for the dropdown
    const getCompanies = async () => {
      const querySnapshot = await getDocs(companyColRef)
      const companiesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as CompanyType[]
      setCompanies(companiesData)
    }

    getCompanies()
  }, [companyColRef])

  // Exchange rate functionality has been removed in favor of manual entry

  // Effect for listening to entries changes
  // Set up the entries listener only once when the component mounts
  useEffect(() => {
    // Subscribe to entries changes
    const q = query(entriesColRef, orderBy('createdAt', 'desc'))

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const entriesData = querySnapshot.docs.map(doc => {
        const data = doc.data() as any // Type cast to avoid TS errors
        return {
          id: doc.id,
          date: data.date.toDate(),
          amount: data.amount,
          currency: data.currency,
          companyId: data.companyId,
          exchangeRate: data.exchangeRate,
          createdAt: data.createdAt?.toDate()
        } as EntryType
      })
      setEntries(entriesData)
    })

    // Clean up the listener when component unmounts
    return () => unsubscribe()
  }, [entriesColRef]) // Only depend on entriesColRef, not entries
  
  // Handle entry deletion
  const handleDeleteEntry = async () => {
    if (!entryToDelete) return
    
    try {
      setIsLoading(true)
      // Get a reference to the entry document
      const entryDocRef = doc(entriesColRef, entryToDelete)
      // Delete the entry
      await deleteDoc(entryDocRef)
      
      toast({
        title: 'Success',
        description: 'Entry deleted successfully',
        status: 'success'
      })
    } catch (error) {
      console.error('Error deleting entry:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete entry',
        status: 'error'
      })
    } finally {
      setIsLoading(false)
      setEntryToDelete(null)
      onDeleteAlertClose()
    }
  }

  const onSubmit = async (data: EntryFormData) => {
    try {
      setIsLoading(true)
      
      // Use the date from the state (managed by DatePicker)
      data.date = selectedDate
      
      // For RON currency, no exchange rate is needed
      if (data.currency === 'RON') {
        data.exchangeRate = 1;
      } else if (!data.exchangeRate) {
        // If not RON and no exchange rate provided, show error
        toast({
          title: 'Error',
          description: 'Exchange rate is required for non-RON currencies',
          status: 'error'
        });
        setIsLoading(false);
        return;
      }
      
      // Add createdAt timestamp
      const newEntry = {
        ...data,
        date: Timestamp.fromDate(data.date),
        createdAt: Timestamp.fromDate(new Date())
      }
      
      await addDoc(entriesColRef, newEntry)
      
      toast({
        title: 'Success',
        description: 'Entry added successfully',
        status: 'success'
      })
      
      reset()
    } catch (error) {
      console.error('Error adding entry:', error)
      toast({
        title: 'Error',
        description: 'Failed to add entry',
        status: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }



  // Calculates RON equivalent based on the entry's exchange rate
  const calculateRonEquivalent = (entry: EntryType) => {
    if (entry.currency === 'RON') return entry.amount
    if (!entry.exchangeRate) return null
    
    return entry.amount * entry.exchangeRate
  }
  
  // Calculate the total RON amount across all entries
  const calculateTotalRonAmount = () => {
    let total = 0
    
    entries.forEach(entry => {
      const ronAmount = calculateRonEquivalent(entry)
      if (ronAmount !== null) {
        total += ronAmount
      }
    })
    
    return total
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date)
  }

  const getCompanyName = (companyId: string) => {
    const company = companies.find(c => c.id === companyId)
    return company?.name || 'Unknown Company'
  }

  // Helper function to render RON amount to simplify complex conditional in JSX
  const renderRonAmount = (entry: EntryType) => {
    const ronAmount = calculateRonEquivalent(entry)
    if (ronAmount !== null) {
      return formatCurrency(ronAmount, 'RON')
    } else if (entry.currency === 'RON') {
      return formatCurrency(entry.amount, 'RON')
    } else {
      return 'N/A'
    }
  }

  // Helper function to render exchange rate to simplify complex conditional in JSX
  const renderExchangeRate = (entry: EntryType) => {
    if (entry.currency === 'RON') {
      return '-'
    } else if (entry.exchangeRate) {
      return `1 ${entry.currency} = ${entry.exchangeRate} RON`
    } else {
      return 'N/A'
    }
  }

  return (
    <Box w="100%">
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Company Entries</Heading>
        <Button
          onClick={onFormToggle}
          size="sm"
        >
          {isFormOpen ? 'Hide Form ▲' : 'Add New Entry ▼'}
        </Button>
      </Flex>

      <Collapse in={isFormOpen} animateOpacity>
        <Box as="form" onSubmit={handleSubmit(onSubmit)} mb={8} p={4} borderWidth="1px" borderRadius="md">
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel>Date</FormLabel>
            <Box border="1px solid" borderColor="gray.200" borderRadius="md">
              <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => date && setSelectedDate(date)}
                dateFormat="dd MMMM yyyy"
                customInput={<Input />}
              />
            </Box>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Amount</FormLabel>
            <NumberInput min={0}>
              <NumberInputField
                {...register('amount', { required: true, min: 0 })}
                placeholder="Enter amount"
              />
            </NumberInput>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Currency</FormLabel>
            <Select
              placeholder="Select currency"
              {...register('currency', { required: true })}
            >
              <option value="GBP">GBP</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="RON">RON</option>
            </Select>
          </FormControl>
          
          <FormControl isRequired={watch('currency') !== 'RON'}>
            <FormLabel>Exchange Rate to RON</FormLabel>
            <NumberInput min={0} precision={4} step={0.0001} isDisabled={watch('currency') === 'RON'}>
              <NumberInputField
                {...register('exchangeRate', { 
                  required: watch('currency') !== 'RON',
                  min: 0 
                })}
                placeholder="Enter exchange rate (e.g. 4.9123)"
              />
            </NumberInput>
            <Text fontSize="sm" color="gray.500">
              1 {watch('currency') || 'Currency'} = ? RON
            </Text>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Company</FormLabel>
            <Select
              placeholder="Select company"
              {...register('companyId', { required: true })}
            >
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Select>
          </FormControl>

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isLoading}
          >
            Add Entry
          </Button>
        </VStack>
      </Box>
      </Collapse>

      <Box overflowX="auto">
        <Heading size="sm" mb={2}>Entries List</Heading>
        {entries.length > 0 ? (
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Date</Th>
                <Th>Company</Th>
                <Th isNumeric>Amount</Th>
                <Th isNumeric>RON Equivalent</Th>
                <Th>Exchange Rate</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {entries.map(entry => {
                return (
                  <Tr key={entry.id}>
                    <Td>{formatDate(entry.date)}</Td>
                    <Td>{getCompanyName(entry.companyId)}</Td>
                    <Td isNumeric>{formatCurrency(entry.amount, entry.currency)}</Td>
                    <Td isNumeric>
                      {renderRonAmount(entry)}
                    </Td>
                    <Td>
                      {renderExchangeRate(entry)}
                    </Td>
                    <Td>
                      <Button
                        aria-label="Delete entry"
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => {
                          setEntryToDelete(entry.id || '')
                          onDeleteAlertOpen()
                        }}
                      >
                        🗑️
                      </Button>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
            {/* Total row */}
            <Tbody>
              <Tr fontWeight="bold">
                <Td colSpan={3} textAlign="right">Total (RON):</Td>
                <Td isNumeric>{formatCurrency(calculateTotalRonAmount(), 'RON')}</Td>
                <Td colSpan={2}></Td>
              </Tr>
            </Tbody>
          </Table>
        ) : (
          <Flex justify="center" p={4}>
            <Text color="gray.500">No entries found</Text>
          </Flex>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Entry
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this entry? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteEntry}
                ml={3}
                isLoading={isLoading}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default CompanyEntries
